"use client";

import type { AvailableRoles } from "@/types";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";

import ClassroomView from "../views/ClassroomView.tsx";
import { useClassroomStore } from "../store/useClassroomStore.tsx";
import VideoRoomFallbackDialog from "../components/VideoRoomFallbackDialog.tsx";
import SwitchLessonDialog from "../components/SwitchLessonDialog.tsx";

import AppLoader from "@/components/AppLoader.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { axiosApiInstance } from "@/libs/axios.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ClassroomWrapper() {
  const { t } = useTranslation(["classroom", "common"]);
  const { user } = useUserStore();
  const { workspaceId, lessonId } = useParams<{ workspaceId: string; lessonId: string }>();
  const redirect = useNavigate();

  const {
    setMeetingId,
    setVideoRoomData,
    setShouldMaintainConnection,
    shouldMaintainConnection,
    isJoined,
    isLoading,
    setIsLoading,
    isInitialized,
    setLessonLocation,
    meetingId,
    lessonLocation: currentLessonLocation,
    videoRoomData,
    meetingError,
    clearMeetingError,
  } = useClassroomStore();
  const [isSwitchLessonDialogOpen, setIsSwitchLessonDialogOpen] = useState(false);
  const [isSwitchingLesson, setIsSwitchingLesson] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [showFallbackDialog, setShowFallbackDialog] = useState(false);

  // Ref to prevent double initialization
  const isInitializingRef = useRef(false);

  const getLessonVideoRoom = async (
    workspaceId: string,
    lessonId: string,
    role: AvailableRoles | undefined,
  ) => {
    try {
      // MOCK: Always throw fallback error for testing
      const MOCK_FALLBACK_MODE = false; // Set to true to test fallback dialog

      if (MOCK_FALLBACK_MODE) {
        console.log("🧪 MOCK: Simulating Cloudflare Realtime creation failure");
        throw {
          response: {
            status: 409,
            data: {
              code: "cloudflare_realtime_creation_failed",
              fallback_url: "https://meet.google.com/test-fallback-room",
            },
          },
        };
      }

      // Update the API endpoint to match the new Cloudflare structure
      const response = await axiosApiInstance(
        `/workspaces/${workspaceId}/lessons/${role === "student" ? "student" : "tutor"}/${lessonId}/video-room`,
      );

      return response.data.data;
    } catch (error: any) {
      console.error("Error getting lesson video room:", error);

      // Handle specific error codes
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        // Handle 401 Unauthorized
        if (status === 401) {
          throw new Error(t("common:errors.unauthenticated"));
        }

        // Handle 403 Forbidden
        if (status === 403) {
          throw new Error(t("classroom:errors.notParticipant"));
        }

        // Handle 404 Not Found
        if (status === 404) {
          throw new Error(t("classroom:errors.lessonNotFound"));
        }

        // Handle 409 Conflict with various error codes
        if (status === 409 && errorData.code) {
          // Special case for Cloudflare Realtime fallback
          if (errorData.code === "cloudflare_realtime_creation_failed" && errorData.fallback_url) {
            throw {
              message: t("classroom:errors.cloudflareRealtimeCreationFailed"),
              fallbackUrl: errorData.fallback_url,
            };
          }

          // Handle other specific error codes
          switch (errorData.code) {
            case "lesson_not_hosted_on_platform":
              throw new Error(t("classroom:errors.lessonNotHostedOnPlatform"));
            case "lesson_not_in_progress":
              throw new Error(t("classroom:errors.lessonNotInProgress"));
            case "lesson_not_started_yet":
              throw new Error(t("classroom:errors.lessonNotStartedYet"));
            case "lesson_already_ended":
              throw new Error(t("classroom:errors.lessonAlreadyEnded"));
            case "no_video_room_available":
              throw new Error(t("classroom:errors.noVideoRoomAvailable"));
            case "cloudflare_realtime_api_error":
              throw new Error(t("classroom:errors.cloudflareRealtimeApiError"));
            case "all_video_solutions_failed":
              throw new Error(t("classroom:errors.allVideoSolutionsFailed"));
            case "cloudflare_realtime_participant_error":
              throw new Error(t("classroom:errors.cloudflareRealtimeParticipantError"));
            case "cloudflare_realtime_token_refresh_error":
              throw new Error(t("classroom:errors.cloudflareRealtimeTokenRefreshError"));
            case "user_not_lesson_participant":
              throw new Error(t("classroom:errors.userNotLessonParticipant"));
            case "video_sdk_creation_failed": // Legacy support
              throw new Error(t("classroom:errors.videoSdkCreationFailed"));
            case "video_sdk_api_error": // Legacy support
              throw new Error(t("classroom:errors.videoSdkApiError"));
            default:
              throw new Error(t("classroom:errors.videoRoomFailed"));
          }
        }

        // Fallback for any other errors
        throw new Error(t("classroom:errors.initializationFailed"));
      }

      throw new Error(t("classroom:errors.initializationFailed"));
    }
  };

  const initializeMeeting = async () => {
    console.log("🎯 initializeMeeting called for lesson:", lessonId);

    // Prevent double initialization
    if (isInitializingRef.current) {
      console.log("🚫 Skipping initialization - already in progress");

      return;
    }

    isInitializingRef.current = true;

    try {
      setIsLoading(true);

      // Reset lesson ended flag when starting a new lesson
      const { setLessonEnded, setToken } = useClassroomStore.getState();

      setLessonEnded(false);

      // Validate required params
      if (!workspaceId || !lessonId) {
        throw new Error(t("classroom:errors.missingParams"));
      }

      try {
        let roomData;

        // Failsafe: Check if we already have correct video room data for this lesson
        if (
          videoRoomData &&
          videoRoomData.custom_room_id === lessonId &&
          videoRoomData.participant_token
        ) {
          console.log("♻️ Using existing video room data for lesson:", lessonId);
          roomData = videoRoomData;
        } else {
          console.log("📡 Fetching new video room data for lesson:", lessonId);
          // Get video room details from API with new Cloudflare structure
          roomData = await getLessonVideoRoom(workspaceId, lessonId, user?.role);
          console.log("📡 Received video room data:", {
            custom_room_id: roomData.custom_room_id,
            has_token: !!roomData.participant_token,
            room_id: roomData.room_id,
          });
        }

        setShouldMaintainConnection(true);

        const { extensionInfo, setExtensionInfo, setView } = useClassroomStore.getState();

        // Check if we have the new participant_token structure
        const authToken = roomData.participant_token;

        if (!authToken) {
          throw new Error(t("classroom:errors.initializationFailed"));
        }

        // Use room_id instead of the old room_id field
        const meetingId = roomData.room_id || roomData.sdk_config?.meeting_id;

        if (extensionInfo && extensionInfo.roomId !== meetingId) {
          setExtensionInfo(null);
        }

        // Store the room data in the global store (only if we fetched new data)
        if (roomData !== videoRoomData) {
          console.log("💾 Storing new video room data in store");
          setVideoRoomData(roomData);
        } else {
          console.log("💾 Skipping video room data storage - using existing data");
        }

        // Log the current state after storing
        setTimeout(() => {
          const currentState = useClassroomStore.getState();

          console.log("💾 Current store state after update:", {
            hasVideoRoomData: !!currentState.videoRoomData,
            videoRoomDataLessonId: currentState.videoRoomData?.custom_room_id,
            hasToken: !!currentState.token,
            hasMeetingId: !!currentState.meetingId,
          });
        }, 0);

        // Set the meeting ID in the store
        console.log("💾 Setting meeting ID:", meetingId);
        setMeetingId(meetingId);

        // Store the token in the store - this will trigger MeetingProvider initialization
        console.log("💾 Setting token in store");
        setToken(authToken);

        // Save the current lesson location for PiP navigation
        const currentLessonLocation = `/${user?.role === "tutor" ? "tutor" : "student"}/workspaces/${workspaceId}/lesson/${lessonId}/video`;

        setLessonLocation(currentLessonLocation);

        setView("prejoin");
      } catch (error: any) {
        console.error("Failed to initialize meeting:", error);

        // Check if this is the Cloudflare Realtime fallback error
        if (
          error.response?.data?.code === "cloudflare_realtime_creation_failed" &&
          error.response?.data?.fallback_url
        ) {
          console.log("Fallback URL detected:", error.response.data.fallback_url);
          setFallbackUrl(error.response.data.fallback_url);

          return; // Don't throw error, let the dialog handle it
        } else if (error.fallbackUrl) {
          console.log("Fallback URL detected:", error.fallbackUrl);
          setFallbackUrl(error.fallbackUrl);

          return; // Don't throw error, let the dialog handle it
        } else {
          throw new Error(error.message || t("classroom:errors.initializationFailed"));
        }
      }
    } finally {
      // Hide loader after initialization
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      // Reset initialization flag
      console.log("🔄 Resetting initialization flag");
      isInitializingRef.current = false;
    }
  };

  const debouncedInitializeMeeting = useDebouncedCallback(initializeMeeting, 500);

  // Use store state to check if we're transitioning from PiP
  const isTransitioningFromPiP = useClassroomStore((state) => state.isTransitioningFromPiP);

  // Ensure loading is immediately set to false when transitioning from PiP
  useEffect(() => {
    if (isTransitioningFromPiP) {
      setIsLoading(false);
    }
  }, [isTransitioningFromPiP, setIsLoading]);

  useEffect(() => {
    const expectedLessonLocation = `/${user?.role === "tutor" ? "tutor" : "student"}/workspaces/${workspaceId}/lesson/${lessonId}/video`;

    // Don't process during lesson switching
    if (isSwitchingLesson) {
      console.log("Skipping initialization during lesson switch");

      return;
    }

    // Check for lesson location mismatch when we have existing data
    if (
      currentLessonLocation &&
      currentLessonLocation !== "" &&
      expectedLessonLocation &&
      currentLessonLocation !== expectedLessonLocation &&
      isJoined &&
      !isSwitchLessonDialogOpen
    ) {
      // Cancel current request and open dialog
      setIsSwitchLessonDialogOpen(true);
      setIsLoading(false);

      return;
    }

    // Don't initialize if dialog is open or already initializing
    if (isSwitchLessonDialogOpen || isInitializingRef.current) {
      console.log("🚫 Skipping initialization check - dialog open or already initializing");

      return;
    }

    // Get current token from store
    const currentToken = useClassroomStore.getState().token;

    // Debug logging for initialization check
    console.log("🔍 Initialization check:", {
      meetingId,
      shouldMaintainConnection,
      hasToken: !!currentToken,
      hasVideoRoomData: !!videoRoomData,
      videoRoomDataLessonId: videoRoomData?.custom_room_id,
      currentLessonId: lessonId,
      isTransitioningFromPiP,
      isSwitchLessonDialogOpen,
      isSwitchingLesson,
    });

    // Check if we need to fetch token/initialize meeting
    // Also check if videoRoomData is for the correct lesson
    const needsInitialization =
      !meetingId ||
      !shouldMaintainConnection ||
      !currentToken ||
      !videoRoomData ||
      videoRoomData.custom_room_id !== lessonId;

    console.log("🔍 Needs initialization:", needsInitialization, {
      noMeetingId: !meetingId,
      noMaintainConnection: !shouldMaintainConnection,
      noToken: !currentToken,
      noVideoRoomData: !videoRoomData,
      lessonMismatch: videoRoomData ? videoRoomData.custom_room_id !== lessonId : false,
    });

    if (needsInitialization) {
      // Only show loading and initialize if we're not returning from PiP
      if (!isTransitioningFromPiP) {
        console.log("🚀 Initializing meeting - needs token, meeting data, or lesson mismatch");
        debouncedInitializeMeeting();
      } else {
        console.log("⏭️ Skipping initialization - returning from PiP");
        // If returning from PiP, skip initialization and loading as meeting is already active
        setIsLoading(false);
        const { setView } = useClassroomStore.getState();

        if (isJoined) {
          setView("call");
        } else {
          setView("prejoin");
        }
      }
    } else {
      console.log("✅ Using existing meeting data - no initialization needed");
      // If we already have a meeting and token, show classroom without loading
      if (isTransitioningFromPiP) {
        // Immediately hide loading when returning from PiP
        setIsLoading(false);
      } else {
        // For normal cases, also hide loading but allow for initialization flow
        setIsLoading(false);
      }

      const { setView } = useClassroomStore.getState();

      if (isJoined) {
        setView("call");
      } else {
        setView("prejoin");
      }
    }
  }, [
    workspaceId,
    lessonId,
    shouldMaintainConnection,
    isJoined,
    isTransitioningFromPiP,
    isSwitchLessonDialogOpen,
    isSwitchingLesson,
    user?.role,
    videoRoomData,
    meetingId,
  ]);

  // Note: Cleanup is now handled by PipModeProvider based on Cloudflare meeting events
  // This prevents premature cleanup when ClassroomWrapper unmounts due to MeetingProvider rendering

  const handleSwitchLessonConfirm = async () => {
    try {
      setIsSwitchLessonDialogOpen(false);
      setIsSwitchingLesson(true);
      setIsLoading(true);

      console.log("Starting lesson switch - leaving current meeting");

      // First, leave the current meeting
      if ((window as any).leaveMeeting) {
        console.log("Leaving current meeting before switching to new lesson");
        (window as any).leaveMeeting();
      }

      // Clear current meeting state to prepare for new lesson
      const { clearMeetingData, setIsJoined, setView, setIsPipMode, setToken, setIsInitialized } =
        useClassroomStore.getState();

      setIsPipMode(false);
      setIsJoined(false);
      setIsInitialized(false); // Reset initialization state
      setView("prejoin");
      clearMeetingData();

      // Clear the token to force refetch for new lesson
      setToken(null);

      // IMPORTANT: Clear the old lesson location to prevent redirect back to original lesson
      setLessonLocation("");

      // Stop maintaining connection to old meeting
      setShouldMaintainConnection(false);

      setIsSwitchingLesson(false);
    } catch (error) {
      console.error("Error switching lesson:", error);
      setIsLoading(false);
      setIsSwitchingLesson(false);
    }
  };

  const handleSwitchLessonCancel = () => {
    setIsSwitchLessonDialogOpen(false);

    // Navigate back to the current lesson location
    const currentLessonLocation = useClassroomStore.getState().lessonLocation;

    if (currentLessonLocation && currentLessonLocation !== "") {
      redirect(currentLessonLocation);
    } else {
      redirect(-1);
    }
  };

  const handleFallbackDialogClose = () => {
    setShowFallbackDialog(false);
    // Redirect to lessons page when dialog is closed
    redirect(
      `/${user?.role === "tutor" ? "tutor" : "student"}/workspaces/${workspaceId}/lessons/listing`,
    );
  };

  // Show fallback dialog if we have a fallback URL
  useEffect(() => {
    if (fallbackUrl && !isLoading) {
      setShowFallbackDialog(true);
    }
  }, [fallbackUrl, isLoading]);

  // Handle meeting error dismissal
  const handleErrorDismiss = () => {
    clearMeetingError();
  };

  return (
    <div className="bg-background h-[100dvh] w-full overflow-hidden">
      <div className="relative h-full w-full">
        {/* Only render ClassroomView when meeting is initialized to prevent hook errors */}
        {isInitialized && <ClassroomView />}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              className="bg-background absolute top-0 left-0 z-50 flex h-full w-full items-center justify-center"
              exit={{ opacity: 0 }}
              initial={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <AppLoader />
            </motion.div>
          )}
        </AnimatePresence>
        <SwitchLessonDialog
          isOpen={isSwitchLessonDialogOpen}
          onClose={handleSwitchLessonCancel}
          onConfirm={handleSwitchLessonConfirm}
        />
        {/* Fallback Dialog */}
        {fallbackUrl && (
          <VideoRoomFallbackDialog
            fallbackUrl={fallbackUrl}
            isOpen={showFallbackDialog}
            onClose={handleFallbackDialogClose}
          />
        )}
        {/* Meeting Error Dialog */}
        {meetingError && (
          <AlertDialog open={!!meetingError} onOpenChange={() => handleErrorDismiss()}>
            <AlertDialogContent className="bg-background border-border max-w-[400px] rounded-xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-accent-foreground">
                  {t("classroom:errors.initializationFailed")}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction
                  className="bg-primary text-accent-foreground hover:bg-primary/90 w-full"
                  onClick={handleErrorDismiss}
                >
                  {t("common:close" as any)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}
